import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import styles from "./CreateNewBudget.module.scss";
import classNames from "classnames/bind";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import { PlusIcon } from "lucide-react";
import { DragIcon, XIcon } from "~/assets/icons";
import React from "react";
import { Box, Button, Modal } from "@mui/material";
import userManagementApi, { ISalesRep } from "~/apis/userManagement";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleGetJobDetails } from "~/thunks/job-details/jobDetailsThunk";
import { handleGetAllEstimate } from "~/thunks/job/jobThunk";
import jobDetailsApi from "~/apis/jobDetails";
import { formatMoney, notify, toDateOnly } from "~/utils/helper";
import { IDetailsBudgetTrackerJobDetails } from "~/utils/interface/jobDetails";
import { EstimateStatusEnum } from "~/pages/admin/jobs/add-job/constants/add-estimate.constants";

const cx = classNames.bind(styles);
// FB-29: flat $ commission (BE JobFinanceBudgetTrackerTypeEnum.FIXED_AMOUNT)
const FIXED_AMOUNT = "Fixed Amount";

const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

interface BudgetLineItem {
  id: string;
  description: string;
  amount: number;
  quantity: number;
  type?: "MATERIAL" | "LABOR";
}

interface commission {
  id: string;
  description: string;
  to: string;
  amountPercentage: number;
  priceType: string;
  amount: number;
}

interface CreateNewBudgetProps {
  jobId: string;
  onCancel: () => void;
  onSave?: () => void;
  idBudget?: string;
  isEdit?: boolean;
}

export interface CreateNewBudgetRef {
  requestLeave: (onLeave: () => void) => void;
  hasUnsavedData: () => boolean;
}

const CreateNewBudget = forwardRef<
  CreateNewBudgetRef,
  CreateNewBudgetProps
>(function CreateNewBudget(
  { jobId, onCancel, onSave, idBudget, isEdit = false },
  ref
) {
  const dispatch = useAppDispatch();
  const { jobDetails } = useAppSelector((state) => state.jobDetails);

  const [lineItems, setLineItems] = useState<BudgetLineItem[]>([
    {
      id: "1",
      description: "",
      amount: 0,
      quantity: 0,
    },
  ]);
  const [commissions, setCommissions] = useState<commission[]>([
    {
      id: "1",
      description: "",
      to: "",
      amountPercentage: 0,
      priceType: "Price",
      amount: 0,
    },
  ]);
  const [salesReps, setSalesReps] = useState<ISalesRep[]>([]);
  const [estimates, setEstimates] = useState<any[]>([]);
  const [priceEntryMode, setPriceEntryMode] = useState<
    "use-existing" | "enter-manually"
  >("enter-manually");
  const [selectedEstimateId, setSelectedEstimateId] = useState<string>("");
  const [budgetDate, setBudgetDate] = useState<string>(getLocalDateString());
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [manualPrice, setManualPrice] = useState<number>(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  const [detailsBudgetTracker, setDetailsBudgetTracker] =
    useState<IDetailsBudgetTrackerJobDetails | null>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingLeaveCallbackRef = useRef<(() => void) | null>(null);
  const hasUnsavedDataRef = useRef(false);
  const allowEstimateLineSyncRef = useRef(!isEdit);
  const initialEditFormRef = useRef<{
    lineItems: BudgetLineItem[];
    commissions: commission[];
    budgetDate: string;
    manualPrice: number;
    priceEntryMode: "use-existing" | "enter-manually";
  } | null>(null);

  const hasUnsavedData = useMemo(() => {
    if (isEdit && detailsBudgetTracker && initialEditFormRef.current) {
      const initial = initialEditFormRef.current;
      const lineItemsChanged =
        lineItems.length !== initial.lineItems.length ||
        lineItems.some(
          (item, i) =>
            initial.lineItems[i] == null ||
            item.description !== initial.lineItems[i].description ||
            item.amount !== initial.lineItems[i].amount ||
            item.quantity !== initial.lineItems[i].quantity ||
            (item.type || "MATERIAL") !==
            (initial.lineItems[i].type || "MATERIAL")
        );
      const commissionsChanged =
        commissions.length !== initial.commissions.length ||
        commissions.some(
          (c, i) =>
            initial.commissions[i] == null ||
            c.description !== initial.commissions[i].description ||
            String(c.to) !== String(initial.commissions[i].to) ||
            c.amountPercentage !== initial.commissions[i].amountPercentage ||
            c.priceType !== initial.commissions[i].priceType
        );
      const basicChanged =
        budgetDate !== initial.budgetDate ||
        manualPrice !== initial.manualPrice ||
        priceEntryMode !== initial.priceEntryMode;
      return lineItemsChanged || commissionsChanged || basicChanged;
    }
    const hasLineItemData = lineItems.some(
      (item) =>
        item.description.trim() !== "" ||
        item.amount > 0 ||
        item.quantity > 0
    );
    const hasPriceData =
      priceEntryMode === "enter-manually"
        ? manualPrice > 0
        : priceEntryMode === "use-existing" && !!selectedEstimateId;
    const hasCommissionData = commissions.some(
      (c) =>
        c.to !== "" ||
        c.amountPercentage > 0 ||
        c.description.trim() !== ""
    );
    return hasLineItemData || hasPriceData || hasCommissionData;
  }, [
    isEdit,
    detailsBudgetTracker,
    lineItems,
    commissions,
    budgetDate,
    manualPrice,
    priceEntryMode,
    selectedEstimateId,
  ]);

  hasUnsavedDataRef.current = hasUnsavedData;

  const handleCancelClick = () => {
    if (hasUnsavedData) {
      pendingLeaveCallbackRef.current = null;
      setShowUnsavedModal(true);
    } else {
      onCancel();
    }
  };

  const requestLeave = (onLeave: () => void) => {
    if (hasUnsavedDataRef.current) {
      pendingLeaveCallbackRef.current = onLeave;
      setShowUnsavedModal(true);
    } else {
      onLeave();
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      requestLeave,
      hasUnsavedData: () => hasUnsavedDataRef.current,
    }),
    []
  );

  const handleConfirmDiscard = () => {
    setShowUnsavedModal(false);
    const cb = pendingLeaveCallbackRef.current;
    pendingLeaveCallbackRef.current = null;
    if (cb) cb();
    onCancel();
  };

  const handleCloseUnsavedModal = () => {
    setShowUnsavedModal(false);
    pendingLeaveCallbackRef.current = null;
  };

  useEffect(() => {
    const fetchDetailsBudgetTracker = async () => {
      try {
        if (idBudget && isEdit) {
          const response =
            await jobDetailsApi.getDetailsBudgetTrackerJobDetails(
              jobId,
              idBudget
            );
          if (response.data?.data) {
            setDetailsBudgetTracker(response.data.data);
          }
        }
      } catch (error: any) {
        console.error("Error fetching details budget tracker:", error.message);
      }
    };
    fetchDetailsBudgetTracker();
  }, [idBudget, isEdit, jobId]);

  useEffect(() => {
    if (isEdit && detailsBudgetTracker) {
      allowEstimateLineSyncRef.current = false;
      // #53: budget_date comes back as UTC-midnight ISO; keep "YYYY-MM-DD" for the date input.
      const budgetDateVal = toDateOnly(detailsBudgetTracker.budgetDate) || getLocalDateString();
      setBudgetDate(budgetDateVal);

      let mappedLineItems: BudgetLineItem[] = [];
      if (detailsBudgetTracker.items && detailsBudgetTracker.items.length > 0) {
        mappedLineItems = detailsBudgetTracker.items.map((item, index) => ({
          id: item.id || (index + 1).toString(),
          description: item.itemName || "",
          amount: parseFloat(item.unitPrice) || 0,
          quantity: item.quantity || 0,
          type: (item.type as "MATERIAL" | "LABOR") || "MATERIAL",
        }));
      } else {
        mappedLineItems = [
          {
            id: "1",
            description: "",
            amount: 0,
            quantity: 0,
          },
        ];
      }
      setLineItems(mappedLineItems);

      let mappedCommissions: commission[] = [];
      if (
        detailsBudgetTracker.commissions &&
        detailsBudgetTracker.commissions.length > 0
      ) {
        mappedCommissions = detailsBudgetTracker.commissions.map(
          (commission, index) => ({
            id: commission.id || (index + 1).toString(),
            description: commission.description || "",
            to: commission.to != null ? String(commission.to) : "",
            // FB-29: a fixed commission stores its dollars in `amount` (amount_percentage is 0)
            amountPercentage:
              commission.of === FIXED_AMOUNT
                ? parseFloat(commission.amount) || 0
                : parseFloat(commission.amountPercentage) || 0,
            priceType:
              commission.of === FIXED_AMOUNT || commission.of === "Gross Profit"
                ? commission.of
                : "Price",
            amount: parseFloat(commission.amount) || 0,
          })
        );
        setCommissions(mappedCommissions);
      } else {
        const defaultCommissions = [
          {
            id: "1",
            description: "",
            to: "",
            amountPercentage: 0,
            priceType: "Price",
            amount: 0,
          },
        ];
        setCommissions(defaultCommissions);
        mappedCommissions = defaultCommissions;
      }

      const manualPriceVal = detailsBudgetTracker.proposePrice
        ? parseFloat(detailsBudgetTracker.proposePrice) || 0
        : 0;
      const restoredMode = detailsBudgetTracker.useExistingEstimate
        ? "use-existing"
        : "enter-manually";

      setManualPrice(manualPriceVal);
      setPriceEntryMode(restoredMode);
      if (restoredMode === "use-existing" && detailsBudgetTracker.estimateId) {
        setSelectedEstimateId(String(detailsBudgetTracker.estimateId));
      } else {
        setSelectedEstimateId("");
      }

      initialEditFormRef.current = {
        lineItems: mappedLineItems,
        commissions: mappedCommissions,
        budgetDate: budgetDateVal,
        manualPrice: manualPriceVal,
        priceEntryMode: restoredMode,
      };
    } else {
      initialEditFormRef.current = null;
    }
  }, [isEdit, detailsBudgetTracker]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedData) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedData]);

  useEffect(() => {
    const popstateKey = "create-budget-tracker";
    window.history.pushState({ [popstateKey]: true }, "");
    const handlePopState = () => {
      if (hasUnsavedDataRef.current) {
        window.history.pushState({ [popstateKey]: true }, "");
        setShowUnsavedModal(true);
      } else {
        onCancel();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [onCancel]);

  useEffect(() => {
    dispatch(handleGetJobDetails(jobId));
  }, [dispatch, jobId]);

  useEffect(() => {
    const fetSales = async () => {
      try {
        const salesRepsResponse = await userManagementApi.getSalesReps();
        if (salesRepsResponse.data?.data?.responses) {
          setSalesReps(salesRepsResponse.data.data.responses);
        }
      } catch (error: any) {
        console.error("Error fetching salesReps:", error.message);
      }
    };

    fetSales();
  }, []);

  useEffect(() => {
    const getAllEstimate = () => {
      dispatch(
        handleGetAllEstimate({
          filter: { jobId: jobId },
        })
      )
        .unwrap()
        .then((response: any) => {
          const list = response?.data?.responses || [];
          setEstimates(
            list.filter(
              (estimate: any) =>
                estimate?.status &&
                estimate.status !== EstimateStatusEnum.DRAFT &&
                estimate.status !== EstimateStatusEnum.VOID
            )
          );
        })
        .catch(() => {
          setEstimates([]);
        });
    };

    getAllEstimate();
  }, [dispatch, jobId]);

  useEffect(() => {
    if (
      !isEdit &&
      priceEntryMode === "use-existing" &&
      estimates.length > 0 &&
      !selectedEstimateId
    ) {
      setSelectedEstimateId(estimates[0].id);
    }
    if (priceEntryMode === "use-existing" && !isEdit) {
      setManualPrice(0);
    }
  }, [priceEntryMode, estimates, selectedEstimateId, isEdit]);

  useEffect(() => {
    const fetchEstimateItems = async () => {
      if (isEdit && detailsBudgetTracker && priceEntryMode !== "use-existing") {
        return;
      }

      if (isEdit && detailsBudgetTracker && !selectedEstimateId) {
        return;
      }

      if (priceEntryMode === "use-existing" && selectedEstimateId) {
        try {
          const response = await jobDetailsApi.getItemCreateNewBudget(
            jobId,
            selectedEstimateId
          );
          if (response.data?.data) {
            if (allowEstimateLineSyncRef.current) {
              const mappedLineItems = response.data.data.map((item, index) => ({
                id: (index + 1).toString(),
                description: item.description,
                amount: item.unitPrice,
                quantity: item.quantity,
                type: item.type || "MATERIAL",
              }));
              setLineItems(mappedLineItems);
            }
          }
        } catch (error: any) {
          console.error("Error fetching estimate items:", error.message);
        }
      }
    };

    fetchEstimateItems();
  }, [priceEntryMode, selectedEstimateId, jobId, isEdit, detailsBudgetTracker]);

  const totalRevenue = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const amount = parseFloat(item.amount.toString()) || 0;
      const qty = parseFloat(item.quantity.toString()) || 0;
      return sum + amount * qty;
    }, 0);
  }, [lineItems]);

  const price = useMemo(() => {
    if (priceEntryMode === "enter-manually") {
      return manualPrice;
    }

    // #55: Price of an estimate = its quoted (selling) total, the same figure shown in the dropdown.
    // The estimate item lineTotals are material/labor COSTS (they pre-fill the budget lines below),
    // so summing them as the price made Gross Profit ~0.
    // Edit: keep the saved proposed price (manualPrice) until the user re-picks an estimate —
    // same rule as the line items (allowEstimateLineSyncRef), so opening Edit never changes saved figures.
    if (isEdit && !allowEstimateLineSyncRef.current) return manualPrice;
    const selectedEstimate = estimates.find(
      (estimate) => String(estimate.id) === String(selectedEstimateId)
    );
    const quotedTotal = Number(
      selectedEstimate?.totalQuoteSum ?? selectedEstimate?.totalAmount ?? 0
    );
    return quotedTotal > 0 ? quotedTotal : manualPrice;
  }, [priceEntryMode, manualPrice, estimates, selectedEstimateId, isEdit]);

  // Gross Profit % = (Gross Profit / Price) × 100
  const grossProfit = useMemo(() => {
    const grossProfitAmount = price - totalRevenue;
    const percentage = price > 0 ? (grossProfitAmount / price) * 100 : 0;

    return {
      amount: grossProfitAmount,
      percentage,
    };
  }, [totalRevenue, price]);

  const calculatedCommissionAmounts = useMemo(() => {
    return commissions.map((commission) => {
      const percentage =
        parseFloat(commission.amountPercentage.toString()) || 0;
      let amount = 0;

      if (commission.priceType === FIXED_AMOUNT) {
        amount = percentage; // the input holds dollars for a fixed commission
      } else if (commission.priceType === "Price") {
        amount = (price * percentage) / 100;
      } else if (commission.priceType === "Gross Profit") {
        amount = (grossProfit.amount * percentage) / 100;
      }

      return {
        ...commission,
        amount,
      };
    });
  }, [commissions, price, grossProfit.amount]);

  const commissionsTotalAmount = useMemo(() => {
    return calculatedCommissionAmounts.reduce(
      (sum, c) => sum + (parseFloat(c.amount.toString()) || 0),
      0
    );
  }, [calculatedCommissionAmounts]);

  // Calculate Net Profit = Gross Profit - Total Commissions
  // Net Profit % = (Net Profit / Price) × 100
  const netProfit = useMemo(() => {
    const totalCommissions = commissionsTotalAmount;

    const netProfitAmount = grossProfit.amount - totalCommissions;
    const percentage = price > 0 ? (netProfitAmount / price) * 100 : 0;

    return {
      amount: netProfitAmount,
      percentage,
    };
  }, [commissionsTotalAmount, price, grossProfit.amount]);

  const getJobDisplayName = () => {
    const customerName =
      jobDetails?.relatedContacts?.primaryContact?.name ||
      jobDetails?.relatedContacts?.secondaryContact?.name ||
      "";
    const jobUniqueID = jobDetails?.jobId || "";

    if (customerName && jobUniqueID) {
      return `${customerName} - ${jobUniqueID}`;
    }
    if (customerName) {
      return customerName;
    }
    if (jobUniqueID) {
      return jobUniqueID;
    }
    return "";
  };

  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [draggedCommissionId, setDraggedCommissionId] = useState<string | null>(
    null
  );

  const generateBudgetNumber = (): string => {
    const year = new Date().getFullYear();
    const sequentialNum = (Date.now() % 10000).toString().padStart(4, "0");
    return `BUD-${year}-${sequentialNum}`;
  };

  const handleAddLineItem = () => {
    const newLineItem: BudgetLineItem = {
      id: Date.now().toString(),
      description: "",
      amount: 0,
      quantity: 1,
    };
    setLineItems([...lineItems, newLineItem]);
  };

  const handleLineItemChange = (
    id: string,
    field: keyof BudgetLineItem,
    value: string | number
  ) => {
    clearError(`item-${id}-${field}`);
    setLineItems(
      lineItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const calculateSubTotal = (
    item: BudgetLineItem
  ): { amount: number; percentage: number } => {
    const itemTotal =
      (parseFloat(item.amount.toString()) || 0) *
      (parseFloat(item.quantity.toString()) || 0);
    const percentage = price > 0 ? (itemTotal / price) * 100 : 0;

    return {
      amount: itemTotal,
      percentage,
    };
  };

  const formatNumber = (value: number): string => {
    if (isNaN(value) || value === null || value === undefined) {
      return "0";
    }
    const numStr = value.toString();
    const parts = numStr.split(".");

    let integerPart = parts[0];
    const isNegative = integerPart.startsWith("-");
    if (isNegative) {
      integerPart = integerPart.substring(1);
    }
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    const result = parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
    return isNegative ? `-${result}` : result;
  };

  const formatCurrency = (value: number): string | React.ReactNode =>
    value < 0 ? (
      <span className={cx("negative-amount")}>{formatMoney(value)}</span>
    ) : (
      formatMoney(value)
    );

  const formatPercentage = (value: number): string => {
    const parts = value.toFixed(2).split(".");
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const formattedValue = `${integerPart}.${parts[1]}`;
    return `(${formattedValue}%)`;
  };

  const handleDeleteLineItem = (id: string) => {
    setLineItems(lineItems.filter((item) => item.id !== id));
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, item: BudgetLineItem) => {
    setDraggedItemId(item.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", "");
  };

  const handleDragOver = (e: React.DragEvent, item: BudgetLineItem) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedItemId && item.id !== draggedItemId) {
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY;
      const itemMiddle = rect.top + rect.height / 2;

      const currentIndex = lineItems.findIndex((i) => i.id === draggedItemId);
      const targetIndex = lineItems.findIndex((i) => i.id === item.id);

      let insertIndex: number;

      if (mouseY < itemMiddle) {
        insertIndex = targetIndex;
      } else {
        insertIndex = targetIndex + 1;
      }

      // Adjust insertIndex if dragging from before target
      if (currentIndex < insertIndex) {
        insertIndex = insertIndex - 1;
      }

      // Only update if position actually changed
      if (currentIndex !== insertIndex && insertIndex >= 0) {
        const newItems = [...lineItems];
        const draggedItem = newItems[currentIndex];
        newItems.splice(currentIndex, 1);
        newItems.splice(insertIndex, 0, draggedItem);
        setLineItems(newItems);
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedItemId(null);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
  };

  // Commission handlers
  const handleAddCommission = () => {
    const newCommission: commission = {
      id: Date.now().toString(),
      description: "",
      to: "",
      amountPercentage: 0,
      priceType: "Price",
      amount: 0,
    };
    setCommissions([...commissions, newCommission]);
  };

  const handleCommissionChange = (
    id: string,
    field: keyof commission,
    value: string | number
  ) => {
    clearError(`commission-${id}-${field}`);
    setCommissions(
      commissions.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleDeleteCommission = (id: string) => {
    setCommissions(commissions.filter((item) => item.id !== id));
  };

  // Commission drag and drop handlers
  const handleCommissionDragStart = (e: React.DragEvent, item: commission) => {
    setDraggedCommissionId(item.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", "");
  };

  const handleCommissionDragOver = (e: React.DragEvent, item: commission) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedCommissionId && item.id !== draggedCommissionId) {
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY;
      const itemMiddle = rect.top + rect.height / 2;

      const currentIndex = commissions.findIndex(
        (i) => i.id === draggedCommissionId
      );
      const targetIndex = commissions.findIndex((i) => i.id === item.id);

      let insertIndex: number;

      if (mouseY < itemMiddle) {
        insertIndex = targetIndex;
      } else {
        insertIndex = targetIndex + 1;
      }

      if (currentIndex < insertIndex) {
        insertIndex = insertIndex - 1;
      }

      if (currentIndex !== insertIndex && insertIndex >= 0) {
        const newItems = [...commissions];
        const draggedItem = newItems[currentIndex];
        newItems.splice(currentIndex, 1);
        newItems.splice(insertIndex, 0, draggedItem);
        setCommissions(newItems);
      }
    }
  };

  const handleCommissionDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedCommissionId(null);
  };

  const handleCommissionDragEnd = () => {
    setDraggedCommissionId(null);
  };

  const calculateCosts = useMemo(() => {
    let materialCost = 0;
    let laborCost = 0;

    lineItems.forEach((item) => {
      const itemTotal =
        (parseFloat(item.amount.toString()) || 0) *
        (parseFloat(item.quantity.toString()) || 0);
      const type = item.type || "MATERIAL";

      if (type === "MATERIAL") {
        materialCost += itemTotal;
      } else if (type === "LABOR") {
        laborCost += itemTotal;
      } else {
        materialCost += itemTotal;
      }
    });

    return { materialCost, laborCost };
  }, [lineItems]);

  // Untouched rows are ignored (they add 0 to every total); anything partially filled must be complete.
  const isBlankLineItem = (item: BudgetLineItem) =>
    !item.description.trim() && !item.amount && !item.quantity;
  const isBlankCommission = (c: commission) =>
    !c.description.trim() && !c.to && !c.amountPercentage;

  const validateForm = (): boolean => {
    const next: Record<string, string> = {};

    if (!budgetDate?.trim()) next.budgetDate = "Budget date is required";
    if (priceEntryMode === "use-existing") {
      if (!selectedEstimateId?.trim()) next.estimate = "Please select an estimate";
    } else if (!manualPrice || manualPrice <= 0) {
      next.price = "Price must be greater than 0";
    }

    const filledItems = lineItems.filter((item) => !isBlankLineItem(item));
    if (filledItems.length === 0 && lineItems[0]) {
      next[`item-${lineItems[0].id}-description`] = "At least one line item is required";
    }
    filledItems.forEach((item) => {
      if (!item.description.trim()) next[`item-${item.id}-description`] = "Required";
      if (!(item.amount > 0)) next[`item-${item.id}-amount`] = "Must be greater than 0";
      if (!(item.quantity > 0)) next[`item-${item.id}-quantity`] = "Must be greater than 0";
    });

    commissions
      .filter((c) => !isBlankCommission(c))
      .forEach((c) => {
        if (!c.description.trim()) next[`commission-${c.id}-description`] = "Required";
        if (!c.to) next[`commission-${c.id}-to`] = "Required";
        if (!(c.amountPercentage > 0)) next[`commission-${c.id}-amountPercentage`] = "Must be greater than 0";
      });

    setErrors(next);
    if (Object.keys(next).length > 0) {
      notify("error", "Please fill in the required fields");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);

      const budgetNumber =
        isEdit && detailsBudgetTracker?.number
          ? detailsBudgetTracker.number
          : generateBudgetNumber();

      const mappedItems = lineItems
        .filter((item) => !isBlankLineItem(item))
        .map((item) => {
          let itemType: "MATERIAL" | "LABOR" = "MATERIAL";
          if (item.type === "LABOR") {
            itemType = "LABOR";
          } else {
            itemType = "MATERIAL";
          }

          return {
            description: item.description,
            type: itemType,
            quantity: parseFloat(item.quantity.toString()) || 0,
            unitPrice: parseFloat(item.amount.toString()) || 0,
            lineTotal:
              (parseFloat(item.amount.toString()) || 0) *
              (parseFloat(item.quantity.toString()) || 0),
          };
        });

      const mappedCommissions = calculatedCommissionAmounts
        .filter((commission) => {
          const toId = parseInt(String(commission.to), 10);
          return Number.isFinite(toId) && toId > 0;
        })
        .map((commission) => ({
          description: commission.description?.trim() || "Commission",
          to: parseInt(String(commission.to), 10),
          amountPercentage:
            commission.priceType === FIXED_AMOUNT
              ? 0
              : parseFloat(commission.amountPercentage.toString()) || 0,
          of: [FIXED_AMOUNT, "Gross Profit"].includes(commission.priceType)
            ? commission.priceType
            : "Price",
          amount: parseFloat(commission.amount.toString()) || 0,
        }));

      const payload = {
        budgetNumber,
        budgetDate,
        gross: grossProfit.amount,
        net: netProfit.amount,
        useExistingEstimate: priceEntryMode === "use-existing",
        estimateId:
          priceEntryMode === "use-existing" && selectedEstimateId
            ? Number(selectedEstimateId)
            : null,
        items: mappedItems,
        commissions: mappedCommissions,
        proposedPrice: price,
        price: price,
        materialCost: calculateCosts.materialCost,
        laborCost: calculateCosts.laborCost,
        expenseCost: 0,
      };

      // Call API
      if (isEdit && idBudget) {
        await jobDetailsApi.updateDetailsBudgetTrackerJobDetails(
          jobId,
          idBudget,
          payload
        );
        notify("success", "Budget updated successfully!");
      } else {
        await jobDetailsApi.createNewBudget(jobId, payload);
        notify("success", "Budget created successfully!");
      }

      // Call onSave callback to trigger list reload and navigation
      if (onSave) {
        onSave();
      } else {
        onCancel();
      }
    } catch (error: any) {
      console.error(`Error ${isEdit ? "updating" : "creating"} budget:`, error);
      notify(
        "error",
        error?.response?.data?.message ||
        `Failed to ${isEdit ? "update" : "create"} budget`
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cx("create-new-budget-container")}>
      <div className={cx("create-new-budget-header")}>
        <h2 className={cx("title")}>
          {isEdit ? "Edit Budget" : "Create New Budget"}
        </h2>
      </div>

      <div className={cx("create-new-budget-body")}>
        <div className={cx("section")}>
          <div className={cx("info-section")}>
            <div className={cx("row")}>
              <label className={cx("label")}>Job</label>
              <p className={cx("divider")}>:</p>
              <p className={cx("value")}>{getJobDisplayName()}</p>
            </div>

            <div className={cx("row")}>
              <label className={cx("label", "mt-[14px]")}>Price <span className="text-red-500">*</span></label>
              <p className={cx("divider", "mt-[14px]")}>:</p>
              <div className={cx("radio-group")}>
                <div className={cx("radio-row")}>
                  <input
                    type="radio"
                    id="use-existing-estimate"
                    name="price-entry-mode"
                    className={cx("radio-input")}
                    checked={priceEntryMode === "use-existing"}
                    onChange={() => {
                      allowEstimateLineSyncRef.current = true;
                      setPriceEntryMode("use-existing");
                    }}
                  />
                  <label
                    htmlFor="use-existing-estimate"
                    className={cx("radio-label")}
                  >
                    Use existing estimate
                  </label>

                  {priceEntryMode === "use-existing" && (
                    <PrimarySelectInput
                      name="existing-estimate"
                      placeholder="Select existing estimate"
                      value={selectedEstimateId}
                      onChange={(value) => {
                        allowEstimateLineSyncRef.current = true;
                        setSelectedEstimateId(value);
                      }}
                      className={cx("select-input")}
                      options={(() => {
                        const options = estimates.map((estimate) => {
                          const total = Number(
                            estimate?.totalQuoteSum ?? estimate?.totalAmount ?? 0
                          );
                          const labelId =
                            estimate?.estimateUniqueId ||
                            estimate?.name ||
                            estimate?.id ||
                            "Estimate";
                          return {
                            value: String(estimate.id),
                            label: `${labelId} $${Number.isFinite(total) ? total : 0}`,
                          };
                        });

                        const savedEstimate = detailsBudgetTracker?.estimate;
                        const savedId = detailsBudgetTracker?.estimateId
                          ? String(detailsBudgetTracker.estimateId)
                          : "";
                        if (
                          savedEstimate &&
                          savedId &&
                          !options.some((option) => option.value === savedId)
                        ) {
                          options.unshift({
                            value: savedId,
                            label: `${
                              savedEstimate.estimateUniqueId ||
                              savedEstimate.name ||
                              savedId
                            }`,
                          });
                        }

                        return options;
                      })()}
                    />
                  )}
                </div>
                <div className={cx("radio-row")}>
                  <input
                    type="radio"
                    id="enter-manually"
                    name="price-entry-mode"
                    className={cx("radio-input")}
                    checked={priceEntryMode === "enter-manually"}
                    onChange={() => setPriceEntryMode("enter-manually")}
                  />
                  <label htmlFor="enter-manually" className={cx("radio-label")}>
                    Enter manually
                  </label>

                  <div className={cx("field")}>
                    <PrimaryInput
                      name="price"
                      placeholder="Enter price"
                      value={manualPrice === 0 ? "$ 0" : `$ ${formatNumber(manualPrice)}`}
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "");
                        setManualPrice(parseFloat(value) || 0);
                        clearError("price");
                      }}
                      className={cx("input")}
                      error={priceEntryMode === "enter-manually" && !!errors.price}
                      disabled={priceEntryMode === "use-existing"}
                    />
                    {priceEntryMode === "enter-manually" && errors.price && (
                      <p className={cx("field-error")}>{errors.price}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={cx("row", "!flex !flex-row !items-center")}>
              <label className={cx("label")}>Budget Date <span className="text-red-500">*</span></label>
              <p className={cx("divider")}>:</p>
              <div className={cx("field")}>
                <PrimaryInput
                  name="budget-date"
                  type="date"
                  placeholder="Enter budget date"
                  value={budgetDate}
                  onChange={(e) => {
                    setBudgetDate(e.target.value);
                    clearError("budgetDate");
                  }}
                  className={cx("input")}
                  error={!!errors.budgetDate}
                />
                {errors.budgetDate && (
                  <p className={cx("field-error")}>{errors.budgetDate}</p>
                )}
              </div>
            </div>
          </div>

          <div className={cx("table-section")}>
            <div className={cx("table-wrapper", "line-items-table")}>
              <div className={cx("table-header")}>
                <div className={cx("table-cell", "drag-cell")}></div>
                <div className={cx("table-cell", "description")}>
                  Description <span className="text-red-500">*</span>
                </div>
                <div className={cx("table-cell", "amount-percentage")}>
                  Amount/Percentage <span className="text-red-500">*</span>
                </div>
                <div className={cx("table-cell", "quantity")}>Quantity <span className="text-red-500">*</span></div>
                <div className={cx("table-cell", "subtotal")}>Sub Total</div>
                <div className={cx("table-cell", "total")}>Total</div>
                <div className={cx("table-cell", "action")}></div>
              </div>

              <div className={cx("table-body")}>
                {lineItems.length > 0 ? (
                  <>
                    {lineItems.map((item) => {
                      const subtotal = calculateSubTotal(item);
                      return (
                        <div
                          key={item.id}
                          className={cx("table-row", {
                            "is-dragging": draggedItemId === item.id,
                          })}
                          draggable
                          onDragStart={(e) => handleDragStart(e, item)}
                          onDragOver={(e) => handleDragOver(e, item)}
                          onDrop={handleDrop}
                          onDragEnd={handleDragEnd}
                        >
                          <div className={cx("table-cell", "drag-cell")}>
                            <button
                              type="button"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <DragIcon className={cx("drag-icon")} />
                            </button>
                          </div>
                          <div className={cx("table-cell", "description")}>
                            <PrimaryInput
                              name={`description-${item.id}`}
                              errorMessage={errors[`item-${item.id}-description`]}
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) =>
                                handleLineItemChange(
                                  item.id,
                                  "description",
                                  e.target.value
                                )
                              }
                              className={cx("table-input")}
                            />
                          </div>
                          <div
                            className={cx("table-cell", "amount-percentage")}
                          >
                            <PrimaryInput
                              name={`amount-${item.id}`}
                              errorMessage={errors[`item-${item.id}-amount`]}
                              placeholder="$ 0"
                              value={
                                item.amount === 0 ? "$ 0" : `$ ${formatNumber(item.amount)}`
                              }
                              onChange={(e) => {
                                let value = e.target.value;
                                value = value
                                  .replace(/^\$\s*/, "")
                                  .replace(/[^0-9.]/g, "");
                                handleLineItemChange(
                                  item.id,
                                  "amount",
                                  parseFloat(value) || 0
                                );
                              }}
                              className={cx("table-input")}
                            />
                          </div>
                          <div className={cx("table-cell", "quantity")}>
                            <PrimaryInput
                              name={`quantity-${item.id}`}
                              errorMessage={errors[`item-${item.id}-quantity`]}
                              placeholder="1"
                              value={formatNumber(item.quantity)}
                              onChange={(e) =>
                                handleLineItemChange(
                                  item.id,
                                  "quantity",
                                  parseFloat(e.target.value.replace(/,/g, "")) || 0
                                )
                              }
                              className={cx("table-input")}
                            />
                          </div>
                          <div className={cx("table-cell", "subtotal")}>
                            <span className={cx("subtotal-value")}>
                              {formatCurrency(subtotal.amount)}{" "}
                              {formatPercentage(subtotal.percentage)}
                            </span>
                          </div>
                          <div className={cx("table-cell", "total")}>
                            <span className={cx("subtotal-value")}>
                              {formatCurrency(subtotal.amount)}
                            </span>
                          </div>
                          <div className={cx("table-cell", "action")}>
                            <button
                              className={cx("delete-button")}
                              onClick={() => handleDeleteLineItem(item.id)}
                              type="button"
                            >
                              <XIcon className={cx("x-icon")} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    <div className={cx("table-row", "table-row-total")}>
                      <div className={cx("table-cell", "drag-cell")} />
                      <div className={cx("table-cell", "description")} />
                      <div className={cx("table-cell", "amount-percentage")} />
                      <div className={cx("table-cell", "quantity")} />
                      <div className={cx("table-cell", "subtotal")} />
                      <div
                        className={cx(
                          "table-cell",
                          "total",
                          "table-cell-total"
                        )}
                      >
                        <p className={cx("subtotal-label")}>Total:</p>
                        <p className={cx("total-value")}>{formatCurrency(totalRevenue)}</p>
                      </div>
                      <div className={cx("table-cell", "action")} />
                    </div>
                  </>
                ) : (
                  <div className={cx("table-row")}>
                    <div className={cx("table-cell", "no-data")}>No data</div>
                  </div>
                )}
              </div>
            </div>

            <div className={cx("add-item-button-container")}>
              <PrimaryButton
                title="Add line item"
                onClick={handleAddLineItem}
                className={cx("add-item-button")}
                icon={<PlusIcon className={cx("plus-icon")} />}
                iconPosition="left"
              />
            </div>

            <div className={cx("total-section")}>
              <label className={cx("label")}>Gross Profit</label>
              <p className={cx("value")}>
                {formatCurrency(grossProfit.amount)}{" "}
                {formatPercentage(grossProfit.percentage)}
              </p>
            </div>
          </div>
        </div>

        <div className={cx("section", "!pt-0")}>
          <div className={cx("section-header")}>
            <h3 className={cx("section-title")}>Commissions</h3>
          </div>

          <div className={cx("table-section")}>
            <div className={cx("table-wrapper", "commissions-table")}>
              <div className={cx("table-header")}>
                <div className={cx("table-cell", "drag-cell")}></div>
                <div className={cx("table-cell", "description")}>
                  Description <span className="text-red-500">*</span>
                </div>
                <div className={cx("table-cell", "to")}>To <span className="text-red-500">*</span></div>
                <div className={cx("table-cell", "amount-percentage")}>
                  Amount/Percentage <span className="text-red-500">*</span>
                </div>
                <div className={cx("table-cell", "amount")}>Amount</div>
                <div className={cx("table-cell", "action")}></div>
              </div>

              <div className={cx("table-body")}>
                {commissions.length > 0 ? (
                  <>
                    {calculatedCommissionAmounts.map((item) => {
                      return (
                        <div
                          key={item.id}
                          className={cx("table-row", {
                            "is-dragging": draggedCommissionId === item.id,
                          })}
                          draggable
                          onDragStart={(e) =>
                            handleCommissionDragStart(e, item)
                          }
                          onDragOver={(e) => handleCommissionDragOver(e, item)}
                          onDrop={handleCommissionDrop}
                          onDragEnd={handleCommissionDragEnd}
                        >
                          <div className={cx("table-cell", "drag-cell")}>
                            <button
                              type="button"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <DragIcon className={cx("drag-icon")} />
                            </button>
                          </div>
                          <div className={cx("table-cell", "description")}>
                            <PrimaryInput
                              name={`commission-description-${item.id}`}
                              errorMessage={errors[`commission-${item.id}-description`]}
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) =>
                                handleCommissionChange(
                                  item.id,
                                  "description",
                                  e.target.value
                                )
                              }
                              className={cx("table-input")}
                            />
                          </div>
                          <div className={cx("table-cell", "to")}>
                            <PrimarySelectInput
                              name={`commission-to-${item.id}`}
                              errorMessage={errors[`commission-${item.id}-to`]}
                              placeholder="Select"
                              value={item.to}
                              onChange={(value) =>
                                handleCommissionChange(item.id, "to", value)
                              }
                              className={cx("table-select")}
                              options={salesReps.map((rep) => ({
                                value: rep.id,
                                label: rep.username,
                              }))}
                            />
                          </div>
                          <div
                            className={cx("table-cell", "amount-percentage")}
                          >
                            <div className={cx("cell-field")}>
                              <div className={cx("amount-percentage-wrapper")}>
                                <div
                                  className={cx("percentage-input-wrapper", {
                                    fixed: item.priceType === FIXED_AMOUNT,
                                  })}
                                >
                                  <PrimaryInput
                                    name={`commission-amount-percentage-${item.id}`}
                                    error={!!errors[`commission-${item.id}-amountPercentage`]}
                                    placeholder="0"
                                    value={
                                      item.amountPercentage === 0
                                        ? ""
                                        : formatNumber(item.amountPercentage)
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value.replace(
                                        /[^0-9.]/g,
                                        ""
                                      );
                                      handleCommissionChange(
                                        item.id,
                                        "amountPercentage",
                                        parseFloat(value) || 0
                                      );
                                    }}
                                    className={cx(
                                      "table-input",
                                      "percentage-input"
                                    )}
                                  />
                                  <span className={cx("percentage-symbol")}>
                                    {item.priceType === FIXED_AMOUNT ? "$" : "%"}
                                  </span>
                                </div>
                                {item.priceType !== FIXED_AMOUNT && (
                                  <span className={cx("percent-of-text")}>of</span>
                                )}
                                <PrimarySelectInput
                                  name={`commission-price-${item.id}`}
                                  placeholder="Select"
                                  value={item.priceType}
                                  onChange={(value) =>
                                    handleCommissionChange(
                                      item.id,
                                      "priceType",
                                      value
                                    )
                                  }
                                  className={cx("table-select", "price-select")}
                                  classNameList={cx("price-select-list")}
                                  options={[
                                    { value: "Price", label: "Price" },
                                    {
                                      value: "Gross Profit",
                                      label: "Gross Profit",
                                    },
                                    { value: FIXED_AMOUNT, label: "Fixed Amount ($)" },
                                  ]}
                                />
                              </div>
                              {errors[`commission-${item.id}-amountPercentage`] && (
                                <p className={cx("cell-error")}>
                                  {errors[`commission-${item.id}-amountPercentage`]}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className={cx("table-cell", "amount")}>
                            <span className={cx("amount-value")}>
                              {formatCurrency(item.amount)}
                            </span>
                          </div>
                          <div className={cx("table-cell", "action")}>
                            <button
                              className={cx("delete-button")}
                              onClick={() => handleDeleteCommission(item.id)}
                              type="button"
                            >
                              <XIcon className={cx("x-icon")} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    <div className={cx("table-row", "table-row-total")}>
                      <div className={cx("table-cell", "drag-cell")} />
                      <div className={cx("table-cell", "description")} />
                      <div className={cx("table-cell", "to")} />
                      <div className={cx("table-cell", "amount-percentage")} />
                      <div
                        className={cx(
                          "table-cell",
                          "amount",
                          "table-cell-total"
                        )}
                      >
                        <p className={cx("subtotal-label")}>Total:</p>
                        <p className={cx("total-value")}>{formatCurrency(commissionsTotalAmount)}</p>
                      </div>
                      <div className={cx("table-cell", "action")} />
                    </div>
                  </>
                ) : (
                  <div className={cx("table-row")}>
                    <div className={cx("table-cell", "no-data")}>No data</div>
                  </div>
                )}
              </div>
            </div>

            <div className={cx("add-item-button-container")}>
              <PrimaryButton
                title="Add commission"
                onClick={handleAddCommission}
                className={cx("add-item-button")}
                icon={<PlusIcon className={cx("plus-icon")} />}
                iconPosition="left"
              />
            </div>

            <div className={cx("total-section")}>
              <label className={cx("label")}>Net Profit</label>
              <p className={cx("value")}>
                {formatCurrency(netProfit.amount)}{" "}
                {formatPercentage(netProfit.percentage)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={cx("create-new-budget-footer")}>
        <PrimaryButton
          title="Cancel"
          onClick={handleCancelClick}
          className={cx("cancel-button")}
          disabled={isSaving}
        />
        <PrimaryButton
          title="Save budget"
          onClick={handleSave}
          className={cx("save-button")}
          loading={isSaving}
        />
      </div>

      <Modal
        open={showUnsavedModal}
        onClose={handleCloseUnsavedModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box className={cx("unsaved-modal")}>
          <h3 className={cx("unsaved-modal-title")}>
            Unsaved changes
          </h3>
          <p className={cx("unsaved-modal-description")}>
            You have unsaved changes. Are you sure you want to leave? All
            changes will be lost.
          </p>
          <div className={cx("unsaved-modal-footer")}>
            <Button
              variant="outlined"
              className={cx("unsaved-modal-cancel")}
              onClick={handleCloseUnsavedModal}
            >
              Stay
            </Button>
            <Button
              variant="contained"
              className={cx("unsaved-modal-confirm")}
              onClick={handleConfirmDiscard}
            >
              Leave without saving
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
});

export default CreateNewBudget;
